<template>
  <div>
    <div class="section-spacing-20 mb-3">
      <CustomerAddressSelect
        v-if="!isEditing && (alwaysShowAddressSelect || !address)"
        v-model="formData.address_id"
        :customer="currentUser"
        name="*Address"
        :error-message="firstErrorMessage(v$.formData?.address_id)"
        data-cy="address"
      />
      <div v-else>
        <b>{{ fullAddress(equipmentAddress) }}</b>
      </div>
      <Select
        v-if="!isEditing"
        v-model="formData.equipment_type_id"
        name="*Equipment Type"
        :options="equipmentTypeOptions"
        :field-error="firstErrorMessage(v$.formData?.equipment_type_id)"
        full-width
        data-cy="equipment_type"
      />
      <div v-else-if="equipment">
        <b>Equipment Type:</b> {{ equipmentTypeLabel }}
      </div>
      <TextField
        v-model="formData.name"
        name="name"
        label="*Equipment Name"
        hint-label="(Water heater, air conditioning unit, etc)"
        class="full-width"
        :validation-errors="v$.$errors"
      />
      <div v-if="supportsWarrantyPrograms">
        <Select
          v-model="formData.warranty.warranty_program_id"
          name="Warranty Program"
          :options="warrantyProgramOptions"
          full-width
          data-cy="warranty-program"
          :placeholder="
            warrantyProgramOptions.length
              ? 'None'
              : 'No Warranty Programs Available - Setup in Company > Programs'
          "
        >
          <template #item-row="{ option }">
            <div class="option-row">
              <truncate-text
                class="ml-1"
                :class="{ 'strike-through': option.archived }"
                :text="option.label"
              />
            </div>
          </template>
        </Select>
        <EquipmentWarrantyForm
          v-if="selectedWarranty"
          v-model="formData.warranty"
          class="pt-4"
          :warranty-program="selectedWarranty"
          :editing="isEditing"
        />
      </div>
      <TextArea
        v-model="formData.notes"
        name="Equipment Notes"
        :validation-errors="v$.$errors"
      />
    </div>
    <div v-if="selectedEquipmentType">
      <h3 class="mb-3">Components ({{ components.length }})</h3>
      <Card
        v-for="(component, index) in components"
        :key="component.name"
        headless
        class="mb-3"
      >
        <EquipmentComponentForm
          :ref="`${component.name}Form`"
          :component="component"
          :show-use-same-manufacturer="index === 0"
          :use-same-manufacturer="useSameManufacturerForAllComponents"
          @update:use-same-manufacturer="useSameManufacturerForAllComponents = $event"
          :validation-errors="v$.$errors"
          @update:form-data="onFormDataUpdated"
        />
      </Card>
      <Card v-if="!components.length" headless>
        <div class="no-results-message">No Components Available</div>
      </Card>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapActions, mapGetters, mapState } from "vuex";
import { getProperty } from "@/lib/helpers.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Card from "@/components/Card.vue";
import TextArea from "@/components/TextArea.vue";
import TextField from "@/components/TextField.vue";
import TruncateText from "@/components/TruncateText.vue";
import CustomerAddressSelect from "@/Customer/AddressSelector/CustomerAddressSelect.vue";
import { dollars } from "@/mixins";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import EquipmentComponentForm from "./EquipmentComponentForm.vue";
import EquipmentWarrantyForm from "./EquipmentWarrantyForm.vue";

export default {
  components: {
    CustomerAddressSelect,
    TextField,
    TextArea,
    EquipmentComponentForm,
    Card,
    EquipmentWarrantyForm,
    TruncateText
  },
  props: {
    alwaysShowAddressSelect: {
      type: Boolean,
      default: false
    },
    equipment: {
      type: Object,
      default: null
    },
    address: {
      type: Object,
      default: () => ({})
    },
    equipmentTypeIdFilter: {
      type: Number,
      default: 0
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      formRefWatchers: [],
      formData: {
        address_id: getProperty(this.address, "id"),
        equipment_type_id: null,
        warranty: {
          warranty_program_id: null,
          purchase_date: null,
          effective_date: null,
          expiration_date: null
        },
        name: "",
        notes: ""
      },
      isEditing: false,
      useSameManufacturerForAllComponents: true
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant", "configSupportedForTenant"]),
    ...mapState({
      currentUser: "currentUser",
      equipmentTypeOptions({ equipmentTypes }) {
        const filteredTypes = equipmentTypes.filter(
          (type) =>
            !type.archived ||
            type.id === getProperty(this.equipment, "equipment_type_id")
        );
        return filteredTypes.map((type) => ({
          label: `${type.name}${type.archived ? " (Archived)" : ""}${
            filteredTypes.length === 1 &&
            type.name.toLowerCase().includes("sera default")
              ? " (Create equipment types to select more options)"
              : ""
          }`,
          value: type.id
        }));
      },
      warrantyProgramOptions({ warrantyPrograms }) {
        return warrantyPrograms.map((program) => ({
          archived: !program.is_active,
          value: program.id,
          label: program.is_active ? program.name : `${program.name} (Archived)`
        }));
      },
      selectedEquipmentType({ equipmentTypes }) {
        const { equipment_type_id } = this.formData;
        if (!equipment_type_id) return null;
        return equipmentTypes.find((type) => type.id === equipment_type_id);
      },
      components() {
        // Returns default component types when creating
        // returns unarchived equipment components when editing
        const equipmentComponents = (
          getProperty(this.equipment, "components") || []
        ).filter((component) => !component.archived);
        const defaultComponents = (
          getProperty(this.selectedEquipmentType, "component_types") || []
        ).map((type) => ({
          component_type_id: type.id,
          name: type.name,
          install_location: "",
          installed_on: "",
          manufactured_on: "",
          manufacturer: "",
          model_number: "",
          serial_number: "",
          warranty: ""
        }));
        return equipmentComponents.length
          ? equipmentComponents
          : defaultComponents;
      },
      equipmentAddress() {
        return this.equipment ? this.equipment.address : this.address;
      },
      equipmentTypeLabel() {
        return getProperty(this.selectedEquipmentType, "name") || "--";
      },
      supportsWarrantyPrograms() {
        // Checks feature flag and if the equipment has the warranty key or not
        let supportsWarrantyPrograms = this.configSupportedForTenant(
          "warranties_available"
        );
        if (
          this.equipment &&
          !Object.prototype.hasOwnProperty.call(this.equipment, "warranty")
        ) {
          supportsWarrantyPrograms = false;
        }
        return supportsWarrantyPrograms;
      },
      selectedWarranty({ warrantyPrograms }) {
        if (!this.formData.warranty.warranty_program_id) {
          return null;
        }
        return warrantyPrograms.find(
          (program) => program.id === this.formData.warranty.warranty_program_id
        );
      }
    })
  },
  watch: {
    "formData.warranty.effective_date"(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.updateWarrantyExpirationDate();
      }
    },
    "selectedWarranty.id"(newValue, oldValue) {
      if (newValue !== oldValue) {
        // Clear warranty dates when changing programs
        this.formData.warranty.purchase_date = null;
        this.formData.warranty.effective_date = null;
        this.formData.warranty.expiration_date = null;
      }
    },
    useSameManufacturerForAllComponents(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.onFormDataUpdated();
      }
    },
    equipment(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setFormData();
      }
    }
  },
  async mounted() {
    await this.fetchEquipmentTypes();
    await this.fetchWarrantyPrograms();
    await this.setFormData();
    this.setDefaultForSameManufacturerCheckbox();
    if (!this.isEditing && !this.address) {
      this.fetchCustomerAddresses({ customerId: this.currentUser.id });
    }
  },
  validations() {
    return {
      formData: {
        address_id: {
          required: helpers.withMessage("*Address is required", required),
          $autoDirty: true
        },
        equipment_type_id: {
          required: helpers.withMessage("*Equipment Type required", required),
          $autoDirty: true
        },
        name: {
          required: helpers.withMessage("*Equipment Name required", required),
          $autoDirty: true
        }
      }
    };
  },
  methods: {
    ...mapActions([
      "fetchEquipmentTypes",
      "fetchWarrantyPrograms",
      "fetchCustomerAddresses"
    ]),
    dollars,
    updateWarrantyExpirationDate() {
      const { effective_date, expiration_date } = this.formData.warranty;
      if (!effective_date || expiration_date || !this.selectedWarranty) return;
      this.formData.warranty.expiration_date = moment(effective_date)
        .add(this.selectedWarranty.term_years, "year")
        .toISOString();
    },
    // One time check through all the saved components to see if the manufacturer is the same
    setDefaultForSameManufacturerCheckbox() {
      if (!this.components?.length) return;
      this.useSameManufacturerForAllComponents = this.components.every(
        (component) =>
          component.manufacturer === this.components[0].manufacturer
      );
    },
    onFormDataUpdated() {
      const formRefs = this.components.map((component) => {
        const ref = this.$refs[`${component.name}Form`];
        return ref ? ref[0] : undefined;
      });
      if (!this.useSameManufacturerForAllComponents) return;
      formRefs.forEach((formRef, index) => {
        if (!formRef) return; // If the reference doesn't exist yet, ignore
        if (index === 0) return; // Don't override the first one because that would be silly
        formRef.formData.manufacturer = formRefs[0].formData.manufacturer;
      });
    },
    async setFormData() {
      if (this.equipment) {
        try {
          this.isEditing = true;
          const { address, equipment_type_id, warranty, name, notes } =
            this.equipment;
          this.formData = {
            address_id: address.id,
            equipment_type_id,
            warranty: {
              warranty_program_id: null,
              purchase_date: null,
              effective_date: null,
              expiration_date: null,
              ...warranty
            },
            name,
            notes
          };
        } catch (e) {
          console.error(e);
        }
      } else {
        if (this.equipmentTypeIdFilter) {
          this.formData.equipment_type_id = this.equipmentTypeIdFilter;
        } else if (this.equipmentTypeOptions.length === 1) {
          this.formData.equipment_type_id = this.equipmentTypeOptions[0].value;
        }
      }
    },
    async getRequestData() {
      const isValid = await this.v$.$validate();
      if (!isValid) return;

      const componentData = [];
      this.components.forEach((component) => {
        const form = this.$refs[`${component.name}Form`];
        if (form && form[0]) {
          componentData.push(form[0].formData);
        }
      });
      return {
        ...this.formData,
        components: componentData
      };
    },
    firstErrorMessage
  }
};
</script>

<style lang="scss" scoped></style>
